import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface ImpersonationState {
  /** The inviter ID being impersonated, or null if not impersonating. */
  inviterId: number | null;
  /** The inviter's display name (for the UI badge). */
  inviterName: string | null;
  startImpersonating: (id: number, name: string) => void;
  stopImpersonating: () => void;
}

const ImpersonationContext = createContext<ImpersonationState>({
  inviterId: null,
  inviterName: null,
  startImpersonating: () => {},
  stopImpersonating: () => {},
});

/**
 * Kept in sessionStorage so a reload stays on the same outreacher. Session,
 * not local: it's per tab, so a second tab opens as the admin, and it ends
 * with the tab. Storage can throw (private mode, blocked site data); then
 * impersonation just doesn't survive a reload.
 */
const STORAGE_KEY = 'spade-impersonating';

interface Stored {
  id: number;
  name: string;
}

export function readStoredImpersonation(): Stored | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<Stored>;
    return typeof v.id === 'number' && Number.isInteger(v.id) && typeof v.name === 'string'
      ? { id: v.id, name: v.name }
      : null;
  } catch {
    return null;
  }
}

function writeStoredImpersonation(value: Stored | null): void {
  try {
    if (value) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Not persisted; the in-memory state still works for this page load.
  }
}

/**
 * The stored identity is read once, before the first render, so the first
 * portal request after a reload already carries the impersonation header and
 * nothing is cached under the admin's identity. Toggling still goes through
 * ViewSwitcher, which invalidates every query (see CLAUDE.md).
 */
export function ImpersonationProvider({ children }: { children: ReactNode }): JSX.Element {
  const [current, setCurrent] = useState<Stored | null>(readStoredImpersonation);

  const startImpersonating = useCallback((id: number, name: string) => {
    const next = { id, name };
    writeStoredImpersonation(next);
    setCurrent(next);
  }, []);

  const stopImpersonating = useCallback(() => {
    writeStoredImpersonation(null);
    setCurrent(null);
  }, []);

  return (
    <ImpersonationContext.Provider
      value={{
        inviterId: current?.id ?? null,
        inviterName: current?.name ?? null,
        startImpersonating,
        stopImpersonating,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation(): ImpersonationState {
  return useContext(ImpersonationContext);
}
